import React, { FC } from "react";
import { ChimneyInsightsBootResult } from "./boot/ChimneyInsightsBootResult";
import { AppLayoutModalProvider } from "shared-ts-ui-app-layout";
import { type LazyRoute, RouterProvider } from "@tanstack/react-router";
import {
  type RouteById,
  router,
  type RouterIds,
} from "chimney-insights-ts-router";
import { OidcClaims } from "firefly-chimney-insights-ts-authentication";
import { RootComponent } from "./RootComponent";
import { useAbnormalBurnsPageEnabled } from "./useAbnormalBurnsPageEnabled";
import { useTaskPlanningPageEnabled } from "./useTaskPlanningPageEnabled";
import { useFlueBurnsPageEnabled } from "./useFlueBurnsPageEnabled";
import { useGenerateErrorPageEnabled } from "./useGenerateErrorPageEnabled";
import { Login } from "./authorization/Login";
import { LoginAction } from "./authorization/LoginAction";
import { setMuiLicenseKey } from "shared-ts-mui";

setMuiLicenseKey();

export interface AppRouterProps {
  bootResult: ChimneyInsightsBootResult;
}

export const AppRouter: FC<AppRouterProps> = (props) => {
  const canReadDevices = props.bootResult.user.hasClaim(OidcClaims.ReadDevices);
  const abnormalBurnsEnabled = useAbnormalBurnsPageEnabled();
  const taskPlanningPageEnabled = useTaskPlanningPageEnabled();
  const flueBurnsPageEnabled = useFlueBurnsPageEnabled();
  const generateErrorPageEnabled = useGenerateErrorPageEnabled();

  // Generic to enforce that the route returned matches the route path
  type LazyRouteFn<TRoutePath extends RouterIds> = () => Promise<
    LazyRoute<RouteById<(typeof router)["routeTree"], TRoutePath>>
  >;

  type RouterMap = {
    // __root__ is a special route that isn't lazy loaded, so we need to manually bind it
    // You could consider adding null to the returned type to have routes without rendering
    [K in Exclude<RouterIds, "__root__">]: LazyRouteFn<K>;
  };

  const routerMap: Partial<RouterMap> = {
    "/": () =>
      import("firefly-chimney-insights-ts-pages").then(
        (d) => d.OverviewPageRoute
      ),
    "/user": () =>
      import("firefly-chimney-insights-ts-pages").then(
        (d) => d.UserPageRoute
      ) as any,
    "/about": () =>
      import("firefly-chimney-insights-ts-pages").then(
        (d) => d.AboutPageRoute
      ) as any,
    "/organization": () =>
      import("firefly-chimney-insights-ts-settings").then(
        (d) => d.SettingsPageRoute
      ) as any,
    "/authentication/login-callback": () =>
      (<Login action={LoginAction.LoginCallback} />) as any,
  };

  if (generateErrorPageEnabled) {
    routerMap["/generate-error"] = () =>
      import("shared-ts-generate-error").then(
        (d) => d.GenerateErrorPageRoute
      ) as any;
  }

  //flue-status url is used by the temperature alarm sms logic
  //if you are refactoring this url, make sure to update the temperature alarm sms logic
  if (canReadDevices) {
    routerMap["/flue-status"] = () =>
      import("firefly-chimney-insights-ts-pages").then(
        (d) => d.FlueStatusPageRoute
      ) as any;
    routerMap["/flue-status/$id"] = () =>
      import("firefly-chimney-insights-ts-pages").then(
        (d) => d.FlueStatusDialogRoute
      ) as any;
    routerMap["/sensor-status"] = () =>
      import("firefly-chimney-insights-ts-pages").then(
        (d) => d.SensorStatusPageRoute
      ) as any;
    routerMap["/sensor-status/$id"] = () =>
      import("firefly-chimney-insights-ts-pages").then(
        (d) => d.ChimneySensorDialogRoute
      ) as any;
    routerMap["/need-for-sweep"] = () =>
      import("firefly-chimney-insights-ts-pages").then(
        (d) => d.NeedForSweepPageRoute
      ) as any;

    routerMap["/sweep-capacity"] = () =>
      import("firefly-chimney-insights-ts-pages").then(
        (d) => d.SweepingCapacityPlanningPageRoute
      ) as any;

    if (taskPlanningPageEnabled) {
      routerMap["/task-planning"] = () =>
        import("firefly-chimney-insights-ts-pages").then(
          (d) => d.TaskPlanningPageRoute
        ) as any;
    }
    if (abnormalBurnsEnabled) {
      routerMap["/abnormal-burns"] = () =>
        import("firefly-chimney-insights-ts-pages").then(
          (d) => d.AbnormalBurnsPageRoute
        ) as any;
    }
    if (flueBurnsPageEnabled) {
      routerMap["/flue-burns"] = () =>
        import("firefly-chimney-insights-ts-pages").then(
          (d) => d.FlueBurnsPageRoute
        ) as any;
    }
  }

  // Given __root__ is a special route that isn't lazy loaded, we need to update it manually
  router.routesById["__root__"].update({
    component: RootComponent,
  });

  Object.entries(routerMap).forEach(([path, component]) => {
    const foundRoute = router.routesById[path as RouterIds];
    foundRoute.lazy(component as any);
  });

  return (
    <AppLayoutModalProvider>
      <RouterProvider router={router} />
    </AppLayoutModalProvider>
  );
};
